import {gsap} from "gsap";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BitmapText} from "pixi.js";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {Game} from "../Game";
import {GameAssets} from "../GameAssets";
import {GameFeatureMode} from "../enums/Enum";
import {Sound} from "../sound/Sound";
import {GameUtils} from "../utils/GameUtils";

export class FSIntroView extends PIXI.Container implements StageLayer {
    private _button: ContinueButton;
    private _clickCallback: Function;
    private _spins: BitmapText;
    private _fsIntro: PIXI.spine.Spine;
    private _hideFsIntroTl: gsap.core.Timeline | null;
    private _stopped: boolean;

    constructor() {
        super();
        this.name = "FSIntroView";
        this.init()
    }

    public onOrientationChanged() {
        this.onResize();
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
            this._spins.position.set(355, 320);
        } else {
            const centerBottom = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
            this._spins.position.set(355, 320);
        }
    }

    public show(callback: Function): gsap.core.Timeline {
        this._stopped = false;
        this.onOrientationChanged();
        this._spins.text = Game.model.data.freespinsLeft + "";

        this.alpha = 1;
        this.visible = true;
        const showFSIntroTL: gsap.core.Timeline = new gsap.core.Timeline();
        Game.keypadPlugin.setZeroBetSpinCounter(-1);
        Game.keypadPlugin.hide();
        showFSIntroTL.add([
            Game.blackBG.showWithTimeline(0.5, 1),
            gsap.to(this._spins, {duration: 1, alpha: 0.5}),
            gsap.to(this._fsIntro, {duration: 1, alpha: 1}),
            () => {
                this._fsIntro.state.setAnimation(0, "fs_intro_loop", true);
            }
        ], 0);
        if (Game.model.gameData.wasFeatureBuy && Game.autoPlayPlugin.isAutoplayRound) {
            showFSIntroTL.to({}, {duration: 1});
            showFSIntroTL.add(() => {
                this._clickCallback = callback;
                this.onButtonClick();
            });
        } else {
            showFSIntroTL.add(() => {
                this._clickCallback = callback;
            }, 0);
            showFSIntroTL.add(() => {
                gsap.to(this._button, {duration: 0.1, alpha: 1});
                this._button.enable(true);
            });
        }

        return showFSIntroTL;
    }

    private onButtonClick(): void {
        this._button.enable(false);
        this._hideFsIntroTl = gsap.timeline();
        this._hideFsIntroTl.add(() => {
            Game.soundPlayer.playSound(Sound.CONTINUE_BUTTON_START_FS);
            Game.soundPlayer.switchMusic(GameFeatureMode.FREESPIN)
            gsap.to([this._spins, this._button], {duration: 0.1, alpha: 0});
            this._fsIntro.state.setAnimation(0, "fs_intro_out", false);
            Game.blackBG.showWithTimeline(0.01, 0.3);
        });

        this._hideFsIntroTl.add(Game.reelView.hide());
        this._hideFsIntroTl.add(() => {
            Game.soundPlayer.playSound(Sound.ELEVATOR_FROM_MAIN_TO_FS);
        });
        this._hideFsIntroTl.add(Game.backgroundElements.playLiftAnim(Game.model.data.nextMode as GameFeatureMode));
        this._hideFsIntroTl.add(() => {
            Game.blackBG.interactive = true;
            Game.blackBG.buttonMode = true;
            Game.blackBG.once('pointerdown', () => {
                this.stopPostFsIntro();
            });
        }, "<1.7")
        this._hideFsIntroTl.add(Game.reelView.show());
        this._hideFsIntroTl.add(() => {
            Game.keypadPlugin.show();
        });

        this._hideFsIntroTl.add(() => {
            gsap.to(this, {
                duration: 0.1, alpha: 0, onComplete: () => {
                    GameUtils.resetSpineItem(this._fsIntro);
                    this.visible = false;
                    Game.keypadPlugin.show();
                }
            });
            this._fsIntro.alpha = 0;
            this._clickCallback();
        });
    }

    private init(): void {
        this.visible = false;
        this._fsIntro = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("fsIntroOutro"));
        this._fsIntro.position.set(345, 360);
        this._fsIntro.alpha = 0;

        this._button = new ContinueButton();
        this._button.addClickCallback(() => this.onButtonClick());
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false)
        this._button.alpha = 0;

        this._spins = new BitmapText(Game.model.data.freespinsLeft + "", {
            fontName: GameAssets.BITMAP_FONT_OPENSANS_800,
            fontSize: 132,
            tint: 0x027101
        });
        this._spins.anchor = new PIXI.Point(0.5, 0.5);
        this._spins.position.set(355, NolimitApplication.isLandscape ? 300 : 320);
        this._spins.alpha = 0;

        this.addChild(this._fsIntro, this._spins, this._button);
        this.onResize();
    }

    private stopPostFsIntro(): void {
        if (this._stopped) {
            return;
        }

        if (this._hideFsIntroTl && this._hideFsIntroTl.isActive()) {
            Game.soundPlayer.playSound(Sound.CONTINUE_BUTTON_START_FS);
            this._hideFsIntroTl.totalProgress(1);
            Game.soundPlayer.stopSound(Sound.ELEVATOR_FROM_MAIN_TO_FS);
            this._hideFsIntroTl = null;
        }
        Game.blackBG.interactive = false;
        Game.blackBG.buttonMode = false;
        this._stopped = true;
    }
}